import React, { useEffect, useState } from "react";
import {
  Inventory2Outlined,
  CloseOutlined,
  SearchOutlined,
  CheckCircleOutlineOutlined,
  ScienceOutlined,
  TuneOutlined,
  DeleteOutlineOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import { BASE_URL } from "../../constants";
import { toast } from "react-toastify";
import ProductDetailsSkeleton from "./ProductDetailsSkeleton";

const ProductDetails = ({
  cardStyle,
  selectedProduct,
  setSelectedProduct,
  products,
  setProducts,
  loadingProduct,
}) => {
  const [testSearch, setTestSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeTestId, setActiveTestId] = useState(null);
  const [productTests, setProductTests] = useState([]);
  const [savingBom, setSavingBom] = useState(false);

  const getQuantityUnit = (test) => {
    if (test.quantityUnit) return test.quantityUnit;
    return selectedProduct?.unit2 ? "" : "unit1";
  };

  useEffect(() => {
    if (!selectedProduct) {
      setProductTests([]);
      setActiveTestId(null);
      return;
    }

    const existingTests = Array.isArray(selectedProduct.tests)
      ? selectedProduct.tests
      : [];

    setProductTests(existingTests);

    if (existingTests.length > 0) {
      setActiveTestId(existingTests[0].testId);
    } else {
      setActiveTestId(null);
    }

    setTestSearch("");
    setSearchResults([]);
  }, [selectedProduct?._id]);

  useEffect(() => {
    const searchTests = async () => {
      const searchText = testSearch.trim();

      if (!searchText) {
        setSearchResults([]);
        return;
      }

      try {
        setSearchLoading(true);

        const response = await fetch(
          `${BASE_URL}api/user/products/test/search?test=${searchText}`,
          {
            method: "GET",
            headers: {},
          },
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Failed to search tests");
        }

        setSearchResults(data.data || []);
      } catch (error) {
        console.error("Search test error:", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const timer = setTimeout(searchTests, 350);

    return () => clearTimeout(timer);
  }, [testSearch]);

  // ============================================================
  // UPDATE PRODUCT TESTS
  // ============================================================

  const updateProductTests = (updatedTests) => {
    setProductTests(updatedTests);

    if (!selectedProduct) {
      return;
    }

    const updatedProduct = {
      ...selectedProduct,
      tests: updatedTests,
    };

    setSelectedProduct(updatedProduct);

    if (setProducts) {
      setProducts((prevProducts) =>
        prevProducts.map((product) => {
          const productId = product._id || product.id;
          const selectedId = selectedProduct._id || selectedProduct.id;

          return productId === selectedId ? updatedProduct : product;
        }),
      );
    }
  };

  // ============================================================
  // ADD TEST TO CURRENT PRODUCT
  // ============================================================

  const handleAddTest = (test) => {
    if (!selectedProduct) return;

    const alreadyExists = productTests.some((item) => item.testId === test._id);

    if (alreadyExists) {
      setActiveTestId(test._id);
      setTestSearch("");
      setSearchResults([]);
      return;
    }

    const newTest = {
      testId: test._id,

      // Keep name locally for immediate UI.
      // You can remove this when your backend populates it.
      testName: test.name,

      bomType: "deterministic",

      productUnitQty: 1,
      quantityUnit: selectedProduct.unit2 ? "" : "unit1",
    };

    const updatedTests = [...productTests, newTest];

    updateProductTests(updatedTests);

    setActiveTestId(test._id);

    setTestSearch("");
    setSearchResults([]);
  };

  // ============================================================
  // SELECT EXISTING MAPPED TEST
  // ============================================================

  const handleSelectMappedTest = (test) => {
    setActiveTestId(test.testId);
  };

  // ============================================================
  // DELETE TEST
  // ============================================================

  const handleRemoveTest = (testId) => {
    const updatedTests = productTests.filter((test) => test.testId !== testId);

    updateProductTests(updatedTests);

    if (activeTestId === testId) {
      setActiveTestId(updatedTests.length > 0 ? updatedTests[0].testId : null);
    }
  };

  // ============================================================
  // UPDATE BOM TYPE
  // ============================================================

  const updateBomType = (testId, bomType) => {
    const updatedTests = productTests.map((test) =>
      test.testId === testId
        ? {
            ...test,
            bomType,
            productUnitQty:
              bomType === "deterministic" ? test.productUnitQty || 1 : null,
          }
        : test,
    );

    updateProductTests(updatedTests);
  };

  // ============================================================
  // UPDATE UNIT 1 QUANTITY
  // ============================================================

  const updateUnitQty = (testId, value) => {
    const updatedTests = productTests.map((test) =>
      test.testId === testId
        ? {
            ...test,
            productUnitQty: value === "" ? "" : Number(value),
          }
        : test,
    );

    updateProductTests(updatedTests);
  };

  const updateQuantityUnit = (testId, quantityUnit) => {
    const updatedTests = productTests.map((test) =>
      test.testId === testId ? { ...test, quantityUnit } : test,
    );

    updateProductTests(updatedTests);
  };

  // ============================================================
  // SAVE BOM
  // ============================================================

  const handleSaveBom = async () => {
    const productId = selectedProduct?._id || selectedProduct?.id;

    if (!productId) {
      toast.error("Please select a product before saving its BOM.");
      return;
    }

    for (const test of productTests) {
      if (!["deterministic", "probabilistic"].includes(test.bomType)) {
        toast.error("Invalid BOM type selected.");
        return;
      }
      if (test.bomType === "deterministic") {
        const quantity = Number(test.productUnitQty);
        const quantityUnit = getQuantityUnit(test);
        if (!Number.isFinite(quantity) || quantity <= 0) {
          toast.error(
            "Enter a quantity greater than zero for every deterministic test.",
          );
          return;
        }
        if (!quantityUnit) {
          toast.error("Select a unit for every deterministic test.");
          return;
        }
      }
    }

    const payload = {
      tests: productTests.map((test) => {
        if (test.bomType === "probabilistic") {
          return {
            testId: test.testId,
            bomType: "probabilistic",
            productUnitQty: null,
            quantityUnit: null,
          };
        }
        return {
          testId: test.testId,
          bomType: "deterministic",
          productUnitQty: Number(test.productUnitQty),
          quantityUnit: getQuantityUnit(test),
        };
      }),
    };

    try {
      setSavingBom(true);
      const response = await fetch(
        `${BASE_URL}api/user/products/${productId}/bom`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to save BOM");
      }

      toast.success(data.message || "BOM saved successfully");
    } catch (error) {
      console.error("Save BOM error:", error);

      toast.error(error.message || "Unable to save BOM. Please try again.");
    } finally {
      setSavingBom(false);
    }
  };

  // ============================================================
  // ACTIVE TEST
  // ============================================================

  const activeTest = productTests.find((test) => test.testId === activeTestId);

  // ============================================================
  // EMPTY PRODUCT
  // ============================================================

  if (!selectedProduct) {
    return (
      <div
        style={{
          ...cardStyle,
          marginTop: "12px",
          minHeight: "420px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            minHeight: "420px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            textAlign: "center",
            padding: "30px",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#f5f3ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "14px",
            }}
          >
            <Inventory2Outlined
              style={{
                fontSize: "30px",
                color: "#7c3aed",
              }}
            />
          </div>

          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#475569",
            }}
          >
            No Product Selected
          </div>

          <div
            style={{
              marginTop: "5px",
              fontSize: "12px",
              color: "#94a3b8",
            }}
          >
            Select a product from the product list.
          </div>
        </div>
      </div>
    );
  }

  if (loadingProduct) {
    return <ProductDetailsSkeleton />;
  }
  return (
    <div
      style={{
        ...cardStyle,
        marginTop: "12px",
        overflow: "hidden",
      }}
    >
      {/* =========================================================
          PRODUCT HEADER
      ========================================================== */}
      <div
        style={{
          minHeight: "70px",
          padding: "0 18px",
          borderBottom: "1px solid #ede9fe",
          background: "#faf9ff",

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: "42px",
              height: "42px",
              minWidth: "42px",
              borderRadius: "9px",
              background: "#f3e8ff",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Inventory2Outlined
              style={{
                fontSize: "23px",
                color: "#7c3aed",
              }}
            />
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 700,
                color: "#334155",

                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedProduct.name || selectedProduct.productName}
            </div>

            <div
              style={{
                marginTop: "3px",
                display: "flex",
                alignItems: "center",
                gap: "6px",

                fontSize: "11px",
                color: "#94a3b8",
              }}
            >
              <span>{selectedProduct.unit1}</span>

              {selectedProduct.unit2 && (
                <>
                  <span>•</span>
                  <span>{selectedProduct.unit2}</span>
                </>
              )}

              <span>•</span>

              <span>{selectedProduct.tests?.length || 0} tests</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setSelectedProduct(null)}
          title="Close"
          style={{
            width: "34px",
            height: "34px",

            border: "1px solid #e2e8f0",
            background: "#ffffff",
            borderRadius: "7px",

            color: "#64748b",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            cursor: "pointer",
          }}
        >
          <CloseOutlined
            style={{
              fontSize: "18px",
            }}
          />
        </button>
      </div>
      {/* =========================================================
          PRODUCT INFORMATION
      ========================================================== */}

      <Grid
        container
        spacing={1}
        sx={{
          padding: "12px 18px",
          borderBottom: "1px solid #f1f5f9",
          background: "#ffffff",
        }}
      >
        {/* =====================================================
      UNIT 1
  ====================================================== */}

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <div
            style={{
              height: "100%",
              minHeight: "60px",
              padding: "8px 11px",
              boxSizing: "border-box",
              borderRadius: "7px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#94a3b8",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Unit 1</span>
              {selectedProduct.primaryUnit === "unit1" && (
                <span
                  style={{
                    flexShrink: 0,
                    padding: "2px 5px",
                    borderRadius: "8px",
                    background: "#dcfce7",
                    color: "#15803d",
                    fontSize: "7px",
                    fontWeight: 700,
                    letterSpacing: "0.2px",
                  }}
                >
                  PRIMARY
                </span>
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedProduct.unit1}
              </span>
            </div>
          </div>
        </Grid>

        {/* =====================================================
      UNIT 2
  ====================================================== */}

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <div
            style={{
              height: "100%",
              minHeight: "60px",
              padding: "8px 11px",
              boxSizing: "border-box",
              borderRadius: "7px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#94a3b8",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Unit 2</span>
              {selectedProduct.primaryUnit === "unit2" &&
                selectedProduct.unit2 && (
                  <span
                    style={{
                      flexShrink: 0,
                      padding: "2px 5px",
                      borderRadius: "8px",
                      background: "#dcfce7",
                      color: "#15803d",
                      fontSize: "7px",
                      fontWeight: 700,
                      letterSpacing: "0.2px",
                    }}
                  >
                    PRIMARY
                  </span>
                )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedProduct.unit2 || "-"}
              </span>
            </div>
          </div>
        </Grid>

        {/* =====================================================
      CONVERSION
  ====================================================== */}

        <Grid item xs={12} sm={6} md={4} lg={3}>
          <div
            style={{
              height: "100%",
              minHeight: "60px",
              padding: "8px 11px",
              boxSizing: "border-box",
              borderRadius: "7px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#94a3b8",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              Conversion
            </div>

            <div
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#334155",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              1 {selectedProduct.unit1} ={" "}
              {selectedProduct.unitConversion ||
                selectedProduct.conversionQty ||
                1}{" "}
              {selectedProduct.unit2 || selectedProduct.unit1}
            </div>
          </div>
        </Grid>

        {/* =====================================================
      TAX
  ====================================================== */}

        <Grid item xs={12} sm={6} md={4} lg={1.5}>
          <div
            style={{
              height: "100%",
              minHeight: "60px",
              padding: "8px 11px",
              boxSizing: "border-box",
              borderRadius: "7px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#94a3b8",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              Tax
            </div>

            <div
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#334155",
              }}
            >
              {selectedProduct.tax ?? 0}%
            </div>
          </div>
        </Grid>

        {/* =====================================================
      DECIMAL
  ====================================================== */}

        <Grid item xs={12} sm={6} md={4} lg={1.5}>
          <div
            style={{
              height: "100%",
              minHeight: "60px",
              padding: "8px 11px",
              boxSizing: "border-box",
              borderRadius: "7px",
              background: selectedProduct.allowDecimal ? "#f5f3ff" : "#f8fafc",
              border: selectedProduct.allowDecimal
                ? "1px solid #ddd6fe"
                : "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#94a3b8",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              Decimal
            </div>

            <div
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: selectedProduct.allowDecimal ? "#7c3aed" : "#64748b",
              }}
            >
              {selectedProduct.allowDecimal ? "✅" : "🚫"}
            </div>
          </div>
        </Grid>

        {/* =====================================================
      STATUS
  ====================================================== */}

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <div
            style={{
              height: "100%",
              minHeight: "60px",
              padding: "8px 11px",
              boxSizing: "border-box",
              borderRadius: "7px",
              background: selectedProduct.isActive ? "#f0fdf4" : "#fef2f2",
              border: selectedProduct.isActive
                ? "1px solid #bbf7d0"
                : "1px solid #fecaca",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#94a3b8",
                marginBottom: "3px",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              Status
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: selectedProduct.isActive ? "#16a34a" : "#dc2626",
                }}
              />

              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: selectedProduct.isActive ? "#15803d" : "#dc2626",
                }}
              >
                {selectedProduct.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </Grid>
      </Grid>
      {/* =========================================================
          BOM HEADER
      ========================================================== */}
      <div
        style={{
          padding: "12px 18px 9px",
          display: "flex",
          alignItems: "center",
          gap: "7px",
        }}
      >
        <ScienceOutlined
          style={{
            fontSize: "21px",
            color: "#7c3aed",
          }}
        />

        <div>
          <div
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#334155",
            }}
          >
            Test BOM
          </div>

          <div
            style={{
              marginTop: "2px",
              fontSize: "11px",
              color: "#94a3b8",
            }}
          >
            Map tests and configure product consumption
          </div>
        </div>
      </div>
      {/* =========================================================
          BOM CONTENT
      ========================================================== */}
      <div
        style={{
          padding: "0 18px 18px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(240px, 0.9fr) minmax(310px, 1.1fr)",
            gap: "12px",
            minHeight: "290px",
          }}
        >
          {/* =====================================================
              LEFT SIDE
          ====================================================== */}

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "#ffffff",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {/* SEARCH */}

            <div
              style={{
                padding: "8px",
                borderBottom: "1px solid #f1f5f9",
                background: "#fafafa",
              }}
            >
              <div
                style={{
                  position: "relative",
                }}
              >
                <SearchOutlined
                  style={{
                    position: "absolute",
                    left: "9px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "16px",
                    color: "#94a3b8",
                    pointerEvents: "none",
                  }}
                />

                <input
                  type="text"
                  value={testSearch}
                  onChange={(e) => setTestSearch(e.target.value)}
                  placeholder="Search tests..."
                  style={{
                    width: "100%",
                    height: "38px",

                    padding: "0 9px 0 31px",

                    border: "1px solid #d8d4fe",
                    borderRadius: "6px",

                    outline: "none",

                    fontSize: "12px",
                    color: "#334155",

                    boxSizing: "border-box",
                    background: "#ffffff",
                  }}
                />
              </div>

              {/* SEARCH RESULTS */}

              {testSearch.trim() && (
                <div
                  style={{
                    marginTop: "6px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    overflow: "hidden",
                    background: "#ffffff",
                    maxHeight: "145px",
                    overflowY: "auto",
                  }}
                >
                  {searchLoading ? (
                    <div
                      style={{
                        padding: "15px",
                        textAlign: "center",
                        fontSize: "10px",
                        color: "#94a3b8",
                      }}
                    >
                      Searching tests...
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div
                      style={{
                        padding: "15px",
                        textAlign: "center",
                        fontSize: "10px",
                        color: "#94a3b8",
                      }}
                    >
                      No tests found
                    </div>
                  ) : (
                    searchResults.map((test) => {
                      const isAdded = productTests.some(
                        (item) => item.testId === test._id,
                      );

                      return (
                        <div
                          key={test._id}
                          onClick={() => handleAddTest(test)}
                          style={{
                            minHeight: "38px",
                            padding: "0 9px",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",

                            borderBottom: "1px solid #f1f5f9",

                            background: isAdded ? "#faf5ff" : "#ffffff",

                            cursor: "pointer",
                          }}
                        >
                          <div
                            style={{
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#334155",
                              }}
                            >
                              {test.name}
                            </div>
                          </div>

                          {isAdded ? (
                            <CheckCircleOutlineOutlined
                              style={{
                                fontSize: "15px",
                                color: "#7c3aed",
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                fontSize: "9px",
                                fontWeight: 700,
                                color: "#7c3aed",
                              }}
                            >
                              ADD
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* MAPPED TESTS */}

            <div
              style={{
                padding: "8px",
              }}
            >
              <div
                style={{
                  padding: "2px 2px 6px",
                  fontSize: "9px",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                Selected Tests ({productTests.length})
              </div>

              <div
                style={{
                  maxHeight: "210px",
                  overflowY: "auto",
                }}
              >
                {productTests.length === 0 ? (
                  <div
                    style={{
                      padding: "25px 10px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: "10px",
                    }}
                  >
                    Search and select a test above.
                  </div>
                ) : (
                  productTests.map((test, index) => {
                    const isActive = activeTestId === test.testId;

                    return (
                      <div
                        key={test.testId}
                        onClick={() => handleSelectMappedTest(test)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",

                          minHeight: "42px",

                          padding: "0 8px",

                          marginBottom: "5px",

                          border: isActive
                            ? "1px solid #c4b5fd"
                            : "1px solid #e2e8f0",

                          borderRadius: "7px",

                          background: isActive ? "#faf5ff" : "#ffffff",

                          cursor: "pointer",

                          transition: "all 0.12s ease",
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            minWidth: "24px",

                            borderRadius: "6px",

                            background: isActive ? "#7c3aed" : "#f1f5f9",

                            color: isActive ? "#ffffff" : "#64748b",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            fontSize: "9px",
                            fontWeight: 700,
                          }}
                        >
                          {index + 1}
                        </div>

                        <div
                          style={{
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: "#334155",

                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {test.testName || test.name || "Test"}
                          </div>

                          <div
                            style={{
                              marginTop: "2px",
                              fontSize: "8px",
                              color:
                                test.bomType === "deterministic"
                                  ? "#7c3aed"
                                  : "#64748b",
                            }}
                          >
                            {test.bomType === "deterministic"
                              ? "Deterministic"
                              : "Probabilistic"}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTest(test.testId);
                          }}
                          style={{
                            width: "25px",
                            height: "25px",

                            border: "1px solid #fee2e2",

                            background: "#fff",

                            borderRadius: "5px",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            color: "#ef4444",

                            cursor: "pointer",
                          }}
                          title="Remove test"
                        >
                          <DeleteOutlineOutlined
                            style={{
                              fontSize: "14px",
                            }}
                          />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* =====================================================
              RIGHT SIDE - CONFIGURATION
          ====================================================== */}

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "#ffffff",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {!activeTest ? (
              <div
                style={{
                  height: "100%",
                  minHeight: "260px",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",

                  flexDirection: "column",

                  textAlign: "center",
                  padding: "25px",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",

                    borderRadius: "50%",

                    background: "#f5f3ff",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    marginBottom: "10px",
                  }}
                >
                  <TuneOutlined
                    style={{
                      fontSize: "23px",
                      color: "#8b5cf6",
                    }}
                  />
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#475569",
                  }}
                >
                  Test Configuration
                </div>

                <div
                  style={{
                    marginTop: "4px",
                    maxWidth: "240px",
                    fontSize: "9px",
                    lineHeight: 1.5,
                    color: "#94a3b8",
                  }}
                >
                  Select a mapped test from the left to configure its BOM
                  behavior.
                </div>
              </div>
            ) : (
              <>
                {/* CONFIG HEADER */}

                <div
                  style={{
                    minHeight: "58px",

                    padding: "0 12px",

                    display: "flex",
                    alignItems: "center",

                    borderBottom: "1px solid #ede9fe",

                    background: "#faf9ff",
                  }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      minWidth: "34px",

                      borderRadius: "7px",

                      background: "#f3e8ff",

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",

                      marginRight: "8px",
                    }}
                  >
                    <ScienceOutlined
                      style={{
                        fontSize: "18px",
                        color: "#7c3aed",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#334155",

                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {activeTest.testName ||
                        activeTest.name ||
                        "Selected Test"}
                    </div>

                    <div
                      style={{
                        marginTop: "2px",
                        fontSize: "9px",
                        color: "#94a3b8",
                      }}
                    >
                      BOM Configuration
                    </div>
                  </div>
                </div>

                {/* CONFIG CONTENT */}

                <div
                  style={{
                    padding: "12px",
                  }}
                >
                  {/* BOM TYPE */}

                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#475569",
                        marginBottom: "7px",
                      }}
                    >
                      Consumption Type
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "7px",
                      }}
                    >
                      {/* DETERMINISTIC */}

                      <label
                        style={{
                          minHeight: "56px",

                          display: "flex",
                          alignItems: "center",

                          gap: "7px",

                          padding: "7px 9px",

                          border:
                            activeTest.bomType === "deterministic"
                              ? "1px solid #8b5cf6"
                              : "1px solid #e2e8f0",

                          borderRadius: "7px",

                          background:
                            activeTest.bomType === "deterministic"
                              ? "#faf5ff"
                              : "#ffffff",

                          cursor: "pointer",

                          boxSizing: "border-box",
                        }}
                      >
                        <input
                          type="radio"
                          name={`bomType-${activeTest.testId}`}
                          value="deterministic"
                          checked={activeTest.bomType === "deterministic"}
                          onChange={() =>
                            updateBomType(activeTest.testId, "deterministic")
                          }
                          style={{
                            width: "16px",
                            height: "16px",
                            margin: 0,
                            accentColor: "#7c3aed",
                            flexShrink: 0,
                          }}
                        />

                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            Deterministic
                          </div>

                          <div
                            style={{
                              marginTop: "2px",
                              fontSize: "9px",
                              color: "#94a3b8",
                            }}
                          >
                            Fixed quantity per test
                          </div>
                        </div>
                      </label>

                      {/* PROBABILISTIC */}

                      <label
                        style={{
                          minHeight: "56px",

                          display: "flex",
                          alignItems: "center",

                          gap: "7px",

                          padding: "7px 9px",

                          border:
                            activeTest.bomType === "probabilistic"
                              ? "1px solid #8b5cf6"
                              : "1px solid #e2e8f0",

                          borderRadius: "7px",

                          background:
                            activeTest.bomType === "probabilistic"
                              ? "#faf5ff"
                              : "#ffffff",

                          cursor: "pointer",

                          boxSizing: "border-box",
                        }}
                      >
                        <input
                          type="radio"
                          name={`bomType-${activeTest.testId}`}
                          value="probabilistic"
                          checked={activeTest.bomType === "probabilistic"}
                          onChange={() =>
                            updateBomType(activeTest.testId, "probabilistic")
                          }
                          style={{
                            width: "16px",
                            height: "16px",
                            margin: 0,
                            accentColor: "#7c3aed",
                            flexShrink: 0,
                          }}
                        />

                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            Probabilistic
                          </div>

                          <div
                            style={{
                              marginTop: "2px",
                              fontSize: "9px",
                              color: "#94a3b8",
                            }}
                          >
                            Variable usage
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* =================================================
                      DETERMINISTIC QUANTITY
                  ================================================== */}

                  {activeTest.bomType === "deterministic" && (
                    <div
                      style={{
                        marginTop: "10px",

                        padding: "9px 10px",

                        border: "1px solid #ddd6fe",

                        borderRadius: "7px",

                        background: "#faf5ff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            Required Quantity
                          </div>

                          <div
                            style={{
                              marginTop: "3px",
                              fontSize: "9px",
                              color: "#64748b",
                              lineHeight: 1.4,
                            }}
                          >
                            Product{" "}
                            {/* <strong
                              style={{
                                color: "#7c3aed",
                              }}
                            >
                              {getQuantityUnit(activeTest) === "unit2"
                                ? selectedProduct.unit2
                                : selectedProduct.unit1}
                            </strong>{" "} */}
                            required for one test.
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            flexShrink: 0,
                          }}
                        >
                          <input
                            type="number"
                            min="0.01"
                            step={selectedProduct.allowDecimal ? "0.01" : "1"}
                            value={activeTest.productUnitQty ?? 1}
                            onChange={(e) =>
                              updateUnitQty(activeTest.testId, e.target.value)
                            }
                            style={{
                              width: "64px",
                              height: "34px",

                              padding: "0 6px",

                              border: "1px solid #c4b5fd",

                              borderRadius: "5px",

                              outline: "none",

                              background: "#ffffff",

                              fontSize: "12px",
                              fontWeight: 700,

                              color: "#334155",

                              textAlign: "center",

                              boxSizing: "border-box",
                            }}
                          />
                          {console.log("selectedProduct ", selectedProduct)}
                          <select
                            value={getQuantityUnit(activeTest)}
                            onChange={(e) =>
                              updateQuantityUnit(
                                activeTest.testId,
                                e.target.value,
                              )
                            }
                            style={{
                              height: "34px",
                              maxWidth: "110px",
                              padding: "0 22px 0 6px",
                              border: "1px solid #c4b5fd",
                              borderRadius: "5px",
                              outline: "none",
                              background: "#ffffff",
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#7c3aed",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            <option value="" selected>
                              Select unit
                            </option>
                            <option value="unit1">
                              {selectedProduct.unit1}
                            </option>
                            {selectedProduct.unit2 && (
                              <option value="unit2">
                                {selectedProduct.unit2}
                              </option>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* =================================================
                      PROBABILISTIC INFO
                  ================================================== */}

                  {activeTest.bomType === "probabilistic" && (
                    <div
                      style={{
                        marginTop: "10px",

                        padding: "9px 10px",

                        border: "1px solid #e2e8f0",

                        borderRadius: "7px",

                        background: "#f8fafc",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "7px",
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            minWidth: "24px",

                            borderRadius: "6px",

                            background: "#ede9fe",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <TuneOutlined
                            style={{
                              fontSize: "13px",
                              color: "#7c3aed",
                            }}
                          />
                        </div>

                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            Variable Consumption
                          </div>

                          <div
                            style={{
                              marginTop: "3px",
                              fontSize: "9px",
                              lineHeight: 1.5,
                              color: "#64748b",
                            }}
                          >
                            This test does not have a fixed product quantity.
                            Actual consumption can vary during processing.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "0 18px 18px",
        }}
      >
        <span
          style={{
            fontSize: "11px",
            color: "#94a3b8",
          }}
        >
          {productTests.length === 1
            ? "1 test configured"
            : `${productTests.length} tests configured`}
        </span>

        <button
          type="button"
          onClick={handleSaveBom}
          disabled={savingBom}
          style={{
            height: "36px",
            padding: "0 16px",
            border: "none",
            borderRadius: "6px",
            background: savingBom ? "#a78bfa" : "#059669",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "5px",
            fontSize: "11px",
            fontWeight: 700,
            cursor: savingBom ? "not-allowed" : "pointer",
            boxShadow: "0 2px 6px rgba(5,150,105,0.18)",
          }}
        >
          <SaveOutlined style={{ fontSize: "16px" }} />
          {savingBom ? "Saving..." : "Save BOM"}
        </button>
      </div>
    </div>
  );
};

export default ProductDetails;
